import React, { useState, useEffect, useCallback } from 'react';

const DoorSnapPopup = ({ onSnapConfirm }) => {
  const [showPopup, setShowPopup] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [snapData, setSnapData] = useState(null);

  // Listen for door snap popup requests
  useEffect(() => {
    const handleShowDoorSnapPopup = (event) => {
      const { wall, doorPosition, doorId } = event.detail;
      
      console.log('🚪 DOOR SNAP POPUP TRIGGERED:', {
        doorId,
        wallSide: wall.wallSide,
        rectangleId: wall.rectangleId,
        distance: Math.round(wall.distance),
        orientation: wall.snapPoint.orientation,
        timestamp: new Date().toLocaleTimeString()
      });
      
      setSnapData({
        wall,
        doorPosition,
        doorId
      });

      // Position popup near the door
      setPopupPosition({
        x: doorPosition.x + 50,
        y: doorPosition.y - 60 // Above the door
      });
      
      setShowPopup(true);
    };

    const handleHideDoorSnapPopup = () => {
      setShowPopup(false);
      setSnapData(null);
    };

    window.addEventListener('showDoorSnapPopup', handleShowDoorSnapPopup);
    window.addEventListener('hideDoorSnapPopup', handleHideDoorSnapPopup);

    return () => {
      window.removeEventListener('showDoorSnapPopup', handleShowDoorSnapPopup);
      window.removeEventListener('hideDoorSnapPopup', handleHideDoorSnapPopup);
    };
  }, []);

  const handleConfirm = useCallback(() => {
    console.log('✅ DOOR SNAP CONFIRMED BY USER:', {
      snapData,
      timestamp: new Date().toLocaleTimeString()
    });
    
    if (snapData && onSnapConfirm) {
      onSnapConfirm(true, snapData);
    }
    setShowPopup(false);
    setSnapData(null);
  }, [snapData, onSnapConfirm]);

  const handleCancel = useCallback(() => {
    console.log('❌ DOOR SNAP CANCELLED BY USER:', {
      snapData,
      timestamp: new Date().toLocaleTimeString()
    });
    
    if (snapData && onSnapConfirm) {
      onSnapConfirm(false, snapData);
    }
    setShowPopup(false);
    setSnapData(null);
  }, [snapData, onSnapConfirm]);

  // Handle keyboard events
  useEffect(() => {
    if (!showPopup) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleCancel();
      } else if (event.key === 'Enter' || event.key === ' ') {
        handleConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showPopup, handleConfirm, handleCancel]);

  if (!showPopup || !snapData) return null;

  const getSnapMessage = (snapData) => {
    if (!snapData) return { title: 'Snap Door to Wall?', description: 'This will attach the door to the room.' };
    
    const wallSide = snapData.wall.wallSide;
    const orientation = snapData.wall.snapPoint.orientation;
    
    return {
      title: `Snap to ${wallSide.toUpperCase()} Wall?`,
      description: `This will attach the door to the ${wallSide} side of the room (${orientation} opening).`
    };
  };

  const popupStyle = {
    position: 'fixed',
    left: `${popupPosition.x}px`,
    top: `${popupPosition.y}px`,
    transform: 'translateX(-50%)',
    background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    padding: '16px 20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    zIndex: 1000,
    fontFamily: 'Arial, sans-serif',
    fontSize: '14px',
    minWidth: '250px',
    maxWidth: '350px',
    animation: 'slideInFromTop 0.3s ease-out'
  };

  const headerStyle = {
    marginBottom: '8px',
    fontWeight: 'bold',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const descriptionStyle = {
    marginBottom: '16px',
    fontSize: '13px',
    opacity: 0.9,
    lineHeight: '1.4'
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end'
  };

  const buttonStyle = {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'bold',
    transition: 'all 0.2s ease'
  };

  const confirmButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.2)',
    color: 'white',
    backdropFilter: 'blur(10px)'
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'rgba(255, 255, 255, 0.8)'
  };

  return (
    <>
      <style>
        {`
          @keyframes slideInFromTop {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
        `}
      </style>
      <div style={popupStyle} data-door-snap-popup>
        <div style={headerStyle}>
          <span>🚪</span>
          {getSnapMessage(snapData).title}
        </div>
        <div style={descriptionStyle}>
          {getSnapMessage(snapData).description}
        </div>
        <div style={buttonContainerStyle}>
          <button 
            style={cancelButtonStyle}
            onClick={handleCancel}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.15)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            No
          </button>
          <button 
            style={confirmButtonStyle}
            onClick={handleConfirm}
            onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            Yes, Snap Door
          </button>
        </div>
      </div>
    </>
  );
};

export default DoorSnapPopup; 